import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { EditableComponent, ResponsiveGrid } from '@adobe/aem-react-editable-components';
import { useDCAPageModelStore } from '@marriott/mi-store-utils';
import { respGridUtil } from '@marriott/mi-headless-utils';
import {
  BSF_ITEM_TYPE,
  NGSF_AB_TEST_TARGET_MBOX,
  NGSF_ITEM_TYPE,
  SEARCHFORM_EXPERIENCE_BSF,
  SEARCHFORM_EXPERIENCE_NGSF,
} from './constants';
import { SearchFormSkeletonLoader } from './SearchFormSkeletonLoader';

// Types matching IMerchandisingDCAContainerProps
interface INGSearchformABTestWrapperProps {
  pagePath: string;
  itemPath: string;
  isAuthorMode: boolean;
  isAuthor: boolean;
  isTargetContentLoaded: boolean;
  cqItems: Record<string, unknown>;
  cqItemsOrder: string[];
  enableOfferVariation: boolean;
  styleclass: string;
  displayEvergreenContent: boolean;
  hideByDefault?: boolean;
  mboxParameter?: string;
  // Additional props that may be passed through
  allowedComponents?: unknown;
  searchFormExperience?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

interface IUpdatedPageModel extends Record<string, unknown> {
  alloyModelJSON: Record<string, Record<string, unknown>>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mboxData?: Array<{
    mboxName: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    uxlResponse?: {
      isTargetContentError?: boolean;
      metaData?: {
        experience?: string;
      };
      responseTokens?: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: any;
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      [key: string]: any;
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
  }>;
}

/**
 * Removes search form items from cqItems and cqItemsOrder
 * Only affects search form components, leaving all other components intact
 */
const removeSearchFormFromItems = (
  cqItems: Record<string, unknown>,
  cqItemsOrder: string[],
  searchFormItemsToRemove: string[]
): { updatedCqItems: Record<string, unknown>; updatedCqItemsOrder: string[] } => {
  const updatedCqItems = { ...cqItems };
  const updatedCqItemsOrder = [...cqItemsOrder];

  // Iterate through each item in cqItems
  Object.keys(updatedCqItems).forEach(itemKey => {
    const item = updatedCqItems[itemKey] as Record<string, unknown> | undefined;

    if (item && typeof item === 'object') {
      // Check if this item has :items (it's a container)
      const items = item[':items'] as Record<string, unknown> | undefined;

      if (items && typeof items === 'object') {
        // Check if any of the items in this container is the search form we want to remove
        const itemsToRemove: string[] = [];

        Object.keys(items).forEach(subItemKey => {
          const subItem = items[subItemKey] as Record<string, unknown> | undefined;
          if (subItem && typeof subItem === 'object') {
            const itemType = subItem[':type'] as string | undefined;
            // Check if this sub-item matches the search form we want to remove
            if (itemType && searchFormItemsToRemove.some(searchFormItemType => itemType === searchFormItemType)) {
              itemsToRemove.push(subItemKey);
            }
          }
        });

        // Remove the search form items from the container
        if (itemsToRemove.length > 0) {
          const updatedItems = { ...items };
          itemsToRemove.forEach(key => {
            delete updatedItems[key];
          });

          // Update the item with the modified items
          updatedCqItems[itemKey] = {
            ...item,
            ':items': updatedItems,
          };
        }
      }
    }
  });

  return { updatedCqItems, updatedCqItemsOrder };
};

/**
 * Processes target-nextGenSearch-mbox mbox parameter to determine which search form to remove
 * based on the DCA target response
 */
const processPhoenixSearchExperience = (
  updatedPageModel: IUpdatedPageModel | undefined,
  mboxParameter: string,
  props: INGSearchformABTestWrapperProps,
  isABTestEnabled: boolean
): INGSearchformABTestWrapperProps | null => {
  let isProcessingComplete = false;
  let searchFormExperience: string | undefined;
  let hasError = false;
  // Determine which search form to remove based on response
  let searchFormItemsToRemove: string[] | null = [];
  let activityName;
  let experienceName;

  if (!isABTestEnabled) {
    searchFormItemsToRemove = [NGSF_ITEM_TYPE]; // Remove next-gen search form
    isProcessingComplete = true; // Mark processing as complete since we're not waiting on mboxData
  } else {
    // Check mboxData for processing completion
    if (updatedPageModel?.mboxData) {
      for (let componentCount = 0; componentCount < updatedPageModel.mboxData.length; componentCount++) {
        const component = updatedPageModel.mboxData[componentCount];
        if (component.mboxName === mboxParameter) {
          // Check if processing is complete (has response or error)
          if (component?.uxlResponse) {
            isProcessingComplete = true;
            if (component?.uxlResponse?.isTargetContentError === true) {
              hasError = true;
            } else {
              searchFormExperience = component?.uxlResponse?.metaData?.experience;
              if (component?.uxlResponse?.metaData?.experience) {
                activityName = component?.uxlResponse?.responseTokens?.['activity.name'];
                experienceName = component?.uxlResponse?.responseTokens?.['experience.name'];
              }
            }
          }
          break;
        }
      }
    }

    // Return null if processing is not complete yet
    if (!isProcessingComplete) {
      return null;
    }

    if (
      hasError ||
      !searchFormExperience ||
      (searchFormExperience !== SEARCHFORM_EXPERIENCE_BSF && searchFormExperience !== SEARCHFORM_EXPERIENCE_NGSF)
    ) {
      // Fallback: keep baselinesearchform, remove ngsearchform
      searchFormItemsToRemove = [NGSF_ITEM_TYPE];
    } else if (searchFormExperience === SEARCHFORM_EXPERIENCE_BSF) {
      // Experience is baseline search form - remove ngsearchform
      searchFormItemsToRemove = [NGSF_ITEM_TYPE];
    } else if (searchFormExperience === SEARCHFORM_EXPERIENCE_NGSF) {
      // Experience is next gen search form - remove baselinesearchform
      searchFormItemsToRemove = [BSF_ITEM_TYPE];
    }
  }

  // Apply search form removal if needed
  if (
    searchFormItemsToRemove.length &&
    props.cqItems &&
    Object.keys(props.cqItems).length > 0 &&
    props.cqItemsOrder &&
    props.cqItemsOrder.length > 0
  ) {
    const { updatedCqItems, updatedCqItemsOrder } = removeSearchFormFromItems(
      props.cqItems,
      props.cqItemsOrder,
      searchFormItemsToRemove
    );

    return {
      ...props,
      cqItems: updatedCqItems,
      cqItemsOrder: updatedCqItemsOrder,
      isTargetContentLoaded: true,
      searchFormExperience,
      activityName,
      experienceName,
    };
  }

  // No removal needed, but mark as processed
  return {
    ...props,
    isTargetContentLoaded: true,
    searchFormExperience,
    activityName,
    experienceName,
  };
};

const DCAContainer: FC<INGSearchformABTestWrapperProps> = props => {
  const {
    isAuthorMode,
    pagePath,
    itemPath,
    cqItemsOrder,
    cqItems,
    isTargetContentLoaded,
    activityName,
    experienceName,
    searchFormExperience,
  } = props;

  // Helper function to check if a container component contains search form components
  // and if search form should be positioned on herobanner
  const containerHasSearchForm = (
    container: Record<string, unknown>
  ): { hasSearchForm: boolean; enableOverlayTop: boolean } => {
    const items = container?.[':items'] as Record<string, unknown> | undefined;
    if (!items) {
      return { hasSearchForm: false, enableOverlayTop: false };
    }

    let hasSearchForm = false;
    let enableOverlayTop = false;

    // Check if any item in the container is a search form component
    Object.values(items).forEach((item: unknown) => {
      const itemObj = item as Record<string, unknown> | undefined;
      const itemType = itemObj?.[':type'] as string | undefined;
      if (itemType?.includes('baselinesearchform') || itemType?.includes('ngsearchform')) {
        hasSearchForm = true;
        // Check if search form should be positioned on herobanner (enableOverlayTop)
        const overlayTop = itemObj?.['enableOverlayTop'] as boolean | undefined;
        if (overlayTop) {
          enableOverlayTop = true;
        }
      }
    });
    return { hasSearchForm, enableOverlayTop };
  };

  const AuthorComponents = () => {
    return (
      <>
        {Array.from({ length: 1 }, (_, index) => {
          return (
            <div>
              <ResponsiveGrid
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                pagePath={pagePath}
                itemPath={`${itemPath}/${cqItemsOrder[index]}`}
                columnCount="12"
                gridClassNames={''}
                config={{
                  isEmpty: () => true,
                  resourceType: 'mi-aem-common-spa/components/container',
                }}
              />
            </div>
          );
        })}
      </>
    );
  };

  const PublishComponents = () => {
    return (
      <>
        {cqItemsOrder?.map((cardName: string, index: number) => {
          if (Object?.prototype?.hasOwnProperty?.call(cqItems, cardName)) {
            const card = cqItems?.[cardName] as Record<string, unknown>;

            // Check if this is a container component that contains search form components
            const { hasSearchForm, enableOverlayTop } = containerHasSearchForm(card);
            const shouldShowSkeletonLoader = !isTargetContentLoaded && !isAuthorMode && hasSearchForm;

            if (shouldShowSkeletonLoader) {
              return <SearchFormSkeletonLoader key={`${cardName}+${index}`} enableOverlayTop={enableOverlayTop} />;
            }

            const gridUtil = respGridUtil({ ...card });
            const gridUtilWithSERPSkeletonLoaderFlag = gridUtil?.cqItems
              ? {
                  ...gridUtil,
                  cqItems: Object.fromEntries(
                    Object.entries((gridUtil.cqItems as Record<string, Record<string, unknown>>) || {}).map(
                      ([key, item]) => [
                        key,
                        item && typeof item === 'object'
                          ? {
                              ...item,
                              ...(activityName && experienceName ? { activityName, experienceName } : {}),
                              ...(searchFormExperience ? { searchFormExperience } : {}),
                            }
                          : item,
                      ]
                    )
                  ),
                }
              : gridUtil;

            return (
              <div key={`${cardName}+${index}`} className={clsx('container-component')}>
                <ResponsiveGrid {...gridUtilWithSERPSkeletonLoaderFlag} />
              </div>
            );
          } else return <></>;
        })}
      </>
    );
  };

  // Check if we should show skeleton loader for search form (without wrapper classes)
  const shouldShowSearchFormSkeleton = !isTargetContentLoaded && !isAuthorMode;

  // If showing search form skeleton, render it directly without wrapper
  if (shouldShowSearchFormSkeleton) {
    return <PublishComponents />;
  }

  return <>{isAuthorMode ? <AuthorComponents /> : <PublishComponents />}</>;
};

const ABTestWrapper: FC<INGSearchformABTestWrapperProps> = props => {
  const { mboxParameter } = props;
  const updatedPageModel = useDCAPageModelStore(state => state.pageModel) as IUpdatedPageModel | undefined;
  const [finalModel, setFinalModel] = useState<INGSearchformABTestWrapperProps>(props);
  const processingCompleteRef = useRef(false);
  const isABTestEnabled = mboxParameter && mboxParameter === NGSF_AB_TEST_TARGET_MBOX ? true : false;

  // Handle target response
  useEffect(() => {
    if (!processingCompleteRef.current) {
      const processedModel = processPhoenixSearchExperience(
        updatedPageModel,
        mboxParameter ?? '',
        props,
        isABTestEnabled
      );
      if (processedModel) {
        setFinalModel(processedModel);
        processingCompleteRef.current = true;
      }
    }
  }, [updatedPageModel, mboxParameter, props]);

  // Reset when props change in author mode
  useEffect(() => {
    if (props.isAuthorMode) {
      setFinalModel(props);
      processingCompleteRef.current = false;
    }
  }, [props]);

  // On first render, use props to let DCA process
  // After processing completes, use finalModel with search form removed
  const modelToRender = processingCompleteRef.current ? finalModel : props;

  return useMemo(() => <DCAContainer {...modelToRender} />, [modelToRender]);
};

const Config = {
  emptyLabel: 'NG Searchform AB Test Wrapper',
  isEmpty: () => true,
  resourceType: `mi-aem-shop-spa/components/content/ngsearchformabtestwrapper`,
};

/**
 * NGSearchFormABTestWrapper Component
 *
 * This wrapper component handles DCA processing and makes a decision to remove
 * either baselinesearchform or ngsearchform from the container items based on the DCA target response.
 *
 * This only affects searchform components within containers, leaving all other components intact.
 *
 * Fallback logic: If there is no target response or if target call fails,
 * it keeps baselinesearchform and removes ngsearchform.
 *
 * This component is specifically designed for homepage usage.
 */
export const NGSearchFormABTestWrapper: FC<INGSearchformABTestWrapperProps> = props => (
  <EditableComponent config={Config} {...props}>
    <ABTestWrapper {...props} />
  </EditableComponent>
);
